import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

import { API_BASE as API } from "../lib/apiBase";
import { reportGatewayError } from "../lib/gatewayErrorReporter"; // Bogue 1202

// ─────────────────────────────────────────────────────────────────────────────
// AUTH RESILIENCE FIX (Bug Vincent #38 + #45) — Eric 2026-05-07
//
// Vincent reported being "éjecté de Knoria" 5 times in a row "sans raison".
// Deep root cause analysis identified THREE compounding bugs:
//
//   1. Initial checkAuth fall-through: on first page load with prev=null,
//      a TRANSIENT error (502, network blip, slow LLM pipeline blocking
//      the worker) flipped user to `false` → ProtectedRoute → /login.
//   2. No automatic token refresh: backend has /api/auth/refresh but
//      the frontend never used it, so any 401 (expired token, momentarily
//      lost cookie) hard-logged the user out.
//   3. ProtectedRoute did not distinguish "checking" from "logged out".
//
// FIX: this file implements (A) silent token refresh on 401 with a
// single-flight queue (concurrent 401s share one refresh), (B) retry-
// with-backoff on initial /auth/me, (C) never set user=false on
// transient errors — only on confirmed 401/403 AFTER refresh attempt.
// ─────────────────────────────────────────────────────────────────────────────

// Vincent P1.10 (Eric 2026-07-31 SPEC STRICK PROD) — Migration JWT →
// cookie httpOnly UNIQUEMENT. Auparavant, un fallback localStorage sous la
// clé `knoria_token` était utilisé pour Brave/Safari ITP. Ce fallback créait
// une surface XSS (toute injection JS pouvait exfiltrer le token). Vu que
// frontend et backend partagent la même origine via l'ingress Kubernetes,
// les cookies sont 1st-party → aucun fallback nécessaire. Le backend ne
// renvoie plus `access_token` dans le body ; le token vit exclusivement dans
// le cookie httpOnly (voir routers/auth.py + platform_admin.py). L'ancienne
// clé `knoria_token` en localStorage est purgée au 1er run pour hygiene.
try { localStorage.removeItem("knoria_token"); } catch { /* SSR-safe */ }

// Eric (2026-05-19) — `_sleep` hoisté ici car utilisé par l'interceptor de
// réponse (retry exponentiel sur erreurs Cloudflare transitoires). Avant il
// n'était défini que plus bas → ReferenceError quand le retry se déclenchait.
const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Vincent B1016 (Eric 2026-02-26 SPEC STRICK PROD) — Télémétrie
// browser-throttled : envoi debounced au backend (1 event max/5s par URL).
// Fire-and-forget (aucun await, aucune levée d'exception) car on ne veut
// SURTOUT pas générer du trafic supplémentaire quand le navigateur signale
// qu'il est déjà saturé. `_noDedup` pour ne pas passer par le single-flight
// (B1018) — chaque event est unique.
const _throttleReportLastAt = new Map();  // url → timestamp
const _reportBrowserThrottle = (url, code) => {  const now = Date.now();
  const last = _throttleReportLastAt.get(url) || 0;
  if (now - last < 5000) return;  // debounce
  _throttleReportLastAt.set(url, now);
  // Timeout court + no-retry pour éviter d'aggraver la saturation.
  axios.post(
    `${API}/api/telemetry/browser-throttle`,
    {
      url: (url || "").slice(0, 200),
      code: (code || "").slice(0, 60),
      user_agent: (typeof navigator !== "undefined" ? navigator.userAgent : "").slice(0, 300),
      at: new Date().toISOString(),
    },
    {
      withCredentials: true,
      timeout: 3000,
      _maxRetries: 0,
      _noDedup: true,
      silent: true,
    },
  ).catch(() => { /* fire-and-forget */ });
};

// Tâche 1434 (Eric 2026-10-02) — ENREGISTRER LES 520, enfin.
//
// La route serveur `/api/sentinel/gateway-error` et la carte qui l'affiche
// existent depuis le bogue 1202 — mais PERSONNE n'appelait la route. La
// collection `gateway_error_reports` est donc restée VIDE en production, et
// quand Eric a signalé « beaucoup beaucoup beaucoup de déconnexions (HTTP
// 520) », la donnée qui aurait tranché en deux minutes (quelle url, combien de
// temps avant l'échec) n'existait pas : il a fallu fouiller les journaux du
// conteneur. Une erreur que seul le navigateur voit doit être rapportée par le
// navigateur, sinon elle n'est NULLE PART.
//
// Un seul envoi par url toutes les 30 s : un flot de 520 ne doit pas se
// transformer en flot de rapports.
const _derniersRapportsPasserelle = new Map();

const _rapporterErreurPasserelle = (error, url, status, dureeMs) => {
  const cle = `${status}:${url}`;
  const maintenant = Date.now();
  if (maintenant - (_derniersRapportsPasserelle.get(cle) || 0) < 30000) return;
  _derniersRapportsPasserelle.set(cle, maintenant);
  const entetes = error?.response?.headers || {};
  const corps = error?.response?.data;
  axios.post(
    `${API}/api/sentinel/gateway-error`,
    {
      url: (url || "").slice(0, 400),
      method: (error?.config?.method || "get").toUpperCase().slice(0, 10),
      status: status || 0,
      duration_ms: Math.round(dureeMs || 0),
      cf_ray: String(entetes["cf-ray"] || "").slice(0, 80),
      cf_error_code: String(entetes["cf-error-code"] || "").slice(0, 20),
      // Cloudflare renvoie une page HTML : ses 400 premiers caractères
      // suffisent à reconnaître laquelle (520, 524, « Worker exceeded »…).
      body_snippet: (typeof corps === "string" ? corps : "").slice(0, 400),
      page_url: (typeof window !== "undefined" ? window.location.href : "").slice(0, 300),
      user_agent: (typeof navigator !== "undefined" ? navigator.userAgent : "").slice(0, 300),
    },
    { withCredentials: true, timeout: 3000, _maxRetries: 0, _noDedup: true, silent: true },
  ).catch(() => { /* fire-and-forget : un rapport perdu ne doit rien casser */ });
};

// Vincent P1.10 — Ancien request interceptor qui injectait
// `Authorization: Bearer <token>` depuis localStorage supprimé. Les appels
// axios s'appuient uniquement sur `withCredentials: true` (déjà en place
// partout dans le code) → le cookie est envoyé automatiquement.

// ─────────────────────────────────────────────────────────────────────────────
// B1041 (Eric 2026-09-02 SPEC STRICK PROD) — Circuit-breaker global sur
// échecs niveau réseau.
//
// Contexte : lors d'un vrai outage backend (pod down, ingress KO, DNS),
// TOUTES les requêtes échouent simultanément sans HTTP status. Chacune
// tombe dans le retry exponentiel (300ms → 20s × 8 tentatives). Avec
// ~10 pollers actifs (video pipeline, transcription progress, heartbeat,
// health, queue…), ça génère jusqu'à 80 requêtes en cascade en < 90s
// → saturation pool sockets Chrome + banner « Tentative 1/8 » trompeur
// (Eric croyait à un bug de session ; c'était juste le backend prod).
//
// Fix : on maintient une fenêtre glissante des échecs niveau réseau. Si
// >= NETWORK_FAILURE_THRESHOLD (6) échecs distincts se produisent en
// < NETWORK_FAILURE_WINDOW_MS (10s), on considère le backend injoignable
// et on TRIP le breaker :
//   • Toute nouvelle erreur réseau est rejetée IMMÉDIATEMENT (sans retry)
//   • Un event `knoria:backend-unreachable` est émis (banner UI dédié)
//   • Le breaker reste ouvert BREAKER_OPEN_MS (30s) puis se referme
//     automatiquement → une prochaine requête sonde le backend ; si elle
//     réussit, la fenêtre glissante est vidée.
//
// Bénéfices : (1) libère le pool sockets, (2) banner clair au lieu de
// « Tentative 1/8 » qui n'avance jamais, (3) protège le backend en
// redémarrage contre une thundering-herd de retries.
// ─────────────────────────────────────────────────────────────────────────────
const NETWORK_FAILURE_WINDOW_MS = 10000;
const NETWORK_FAILURE_THRESHOLD = 6;
const BREAKER_OPEN_MS = 30000;

const _networkFailures = [];     // timestamps (ms) des échecs récents
let _breakerOpenUntil = 0;        // 0 = fermé ; sinon = timestamp jusqu'à réouverture

// B1046 (Eric 2026-09-03 SPEC STRICK PROD) — Détection de navigation en
// cours (reload / unload / route change). Contexte : le script
// `emergent-main.js` injecté par la plateforme force parfois un
// `window.location.reload()` (« api version mismatch: … » visible dans
// la console). Le reload aborte TOUTES les requêtes axios en vol, qui
// arrivent alors avec `err.message === "Network Error"` et SANS
// `err.response`. Notre B1042 filtrait uniquement `ERR_CANCELED` (issu
// d'un `AbortController.abort()` explicite) mais PAS ces aborts de
// navigation — résultat : chaque reload dépose 6+ échecs réseau
// artificiels → circuit-breaker OPEN → banner « Backend injoignable »
// affiché ALORS QUE le backend est parfaitement joignable. Vu en
// preview (backend healthy) ET en prod, donc c'était bien ça la vraie
// cause du symptôme final, pas le tier CPU ni le worker count.
//
// Fix : dès qu'un event `pagehide` / `beforeunload` / `unload` firece
// (ou que `document.visibilityState` passe à "hidden"), on considère
// qu'on est en navigation. Toute erreur réseau sans `.response`
// pendant cette phase est propagée telle quelle SANS retry, SANS
// compteur de breaker, SANS event UI. La flag revient à false si la
// navigation est annulée (`pageshow` avec `persisted=false`).
//
// B1050 (Eric 2026-09-03 SPEC STRICK PROD) — Le check était trop
// agressif. `visibilityState === "hidden"` capture les tab-switch (l'user
// alt-tab pendant sa saisie n'annule PAS son POST login). Et
// `!cfg.url` matche aussi de vrais échecs adapter-level. Résultat :
// LoginPage affichait « Requête interrompue » à tort. Fix : ne se fier
// QU'AU flag `_isNavigating` (déclenché par un event navigation réel)
// + auto-reset après 2s pour éviter un flag « collé » si le reload est
// finalement annulé (rate-limit sessionStorage, par exemple).
let _isNavigating = false;
let _navigatingResetTimer = null;
if (typeof window !== "undefined") {
  const _markNavigating = () => {
    _isNavigating = true;
    if (_navigatingResetTimer) clearTimeout(_navigatingResetTimer);
    _navigatingResetTimer = setTimeout(() => { _isNavigating = false; }, 2000);
  };
  const _clearNavigating = () => {
    _isNavigating = false;
    if (_navigatingResetTimer) {
      clearTimeout(_navigatingResetTimer);
      _navigatingResetTimer = null;
    }
  };
  window.addEventListener("pagehide", _markNavigating);
  window.addEventListener("beforeunload", _markNavigating);
  window.addEventListener("unload", _markNavigating);
  window.addEventListener("pageshow", (e) => {
    if (!e.persisted) _clearNavigating();
  });
}

const _isNavigationAbort = (_error) => {
  // Signal UNIQUE et strict : navigation en cours détectée par un event
  // (`pagehide` / `beforeunload` / `unload`). Le flag est auto-clear
  // 2s après l'event pour éviter tout collage résiduel.
  //
  // ANCIENS SIGNAUX RETIRÉS (B1050) : visibilityState=hidden (tab-switch
  // légitime), config.url vide (vrais échecs adapter). Trop de faux
  // positifs → LoginPage affichait « Requête interrompue » sur des
  // erreurs réelles.
  return _isNavigating;
};

const _recordNetworkFailure = () => {
  const now = Date.now();
  _networkFailures.push(now);
  // Purge les entrées hors fenêtre
  const cutoff = now - NETWORK_FAILURE_WINDOW_MS;
  while (_networkFailures.length && _networkFailures[0] < cutoff) {
    _networkFailures.shift();
  }
};

const _isBreakerOpen = () => {
  if (_breakerOpenUntil === 0) return false;
  if (Date.now() >= _breakerOpenUntil) {
    // Cool-down écoulé → on referme (probe automatique sur prochaine requête)
    _breakerOpenUntil = 0;
    _networkFailures.length = 0;
    try {
      window.dispatchEvent(new CustomEvent("knoria:backend-probe", {}));
    } catch { /* ignore */ }
    return false;
  }
  return true;
};

const _maybeTripBreaker = () => {
  if (_breakerOpenUntil !== 0) return false;  // déjà ouvert
  if (_networkFailures.length < NETWORK_FAILURE_THRESHOLD) return false;
  _breakerOpenUntil = Date.now() + BREAKER_OPEN_MS;
  try {
    window.dispatchEvent(new CustomEvent("knoria:backend-unreachable", {
      detail: {
        failures: _networkFailures.length,
        windowMs: NETWORK_FAILURE_WINDOW_MS,
        reopenAt: _breakerOpenUntil,
        cooldownMs: BREAKER_OPEN_MS,
      },
    }));
  } catch { /* ignore */ }
  // eslint-disable-next-line no-console
  console.warn(
    `[axios] circuit-breaker OPEN — ${_networkFailures.length} network failures in ${NETWORK_FAILURE_WINDOW_MS}ms — pausing retries for ${BREAKER_OPEN_MS}ms`
  );
  return true;
};

const _resetBreakerOnSuccess = () => {
  // Toute réponse réussie ferme le breaker et vide la fenêtre. Le succès
  // prouve que le backend est joignable — le retry storm passé n'est plus
  // pertinent.
  if (_networkFailures.length > 0 || _breakerOpenUntil !== 0) {
    _networkFailures.length = 0;
    if (_breakerOpenUntil !== 0) {
      _breakerOpenUntil = 0;
      try {
        window.dispatchEvent(new CustomEvent("knoria:backend-recovered", {}));
      } catch { /* ignore */ }
    }
  }
};

// Export pour tests unitaires (non utilisé en runtime)
export const __B1041_INTERNALS = {
  _networkFailures,
  _isBreakerOpen,
  _maybeTripBreaker,
  _recordNetworkFailure,
  _resetBreakerOnSuccess,
  _isNavigationAbort,
  NETWORK_FAILURE_WINDOW_MS,
  NETWORK_FAILURE_THRESHOLD,
  BREAKER_OPEN_MS,
};

// ─────────────────────────────────────────────────────────────────────────────
// Vincent P1 CSRF (Eric 2026-07-31) — Double-submit cookie pattern.
// Le backend émet un cookie non-httpOnly `XSRF-TOKEN`. Sur chaque mutation
// (POST/PUT/PATCH/DELETE), on injecte automatiquement le header
// `X-XSRF-TOKEN` en le lisant depuis le cookie.
// ─────────────────────────────────────────────────────────────────────────────
const _getCookie = (name) => {
  if (typeof document === "undefined") return "";
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : "";
};

axios.interceptors.request.use((config) => {
  try {
    // Bogue 1202 — horodatage du départ : c'est la seule façon de savoir si
    // l'erreur Cloudflare tombe immédiatement (plantage de l'origine) ou au
    // bout de ~100 s (dépassement de délai). Deux causes opposées.
    config._startedAt = Date.now();
  } catch { /* silencieux */ }
  try {
    const method = (config.method || "get").toUpperCase();
    if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
      const token = _getCookie("XSRF-TOKEN");
      if (token) {
        config.headers = config.headers || {};
        if (!config.headers["X-XSRF-TOKEN"]) {
          config.headers["X-XSRF-TOKEN"] = token;
        }
      }
    }
  } catch { /* SSR-safe */ }
  return config;
});

// ─────────────────────────────────────────────────────────────────────────────
// B1088 (Eric 2026-09-05) — Provenance des appels : chaque requête API porte
// la page de l'app qui l'a déclenchée, et la PREMIÈRE requête après une
// navigation est marquée. Le serveur peut ainsi calculer « /videos : 14 appels
// par affichage » et faire remonter la page qui bombarde le backend.
// ─────────────────────────────────────────────────────────────────────────────
let _lastPageSignalled = "";
let _lastNavAt = 0;
const LOAD_WINDOW_MS = 5000;
axios.interceptors.request.use((config) => {
  try {
    if (typeof window === "undefined") return config;
    // Les identifiants sont remplacés pour ne pas exploser en milliers de
    // pages distinctes (/videos/abc123 → /videos/:id).
    const page = (window.location.pathname || "/")
      .replace(/\/[0-9a-f]{8,}/gi, "/:id")
      .replace(/\/\d+/g, "/:n")
      .slice(0, 120);
    config.headers = config.headers || {};
    config.headers["X-Knoria-Page"] = page;
    if (_lastPageSignalled !== page) {
      _lastPageSignalled = page;
      _lastNavAt = Date.now();
      config.headers["X-Knoria-Page-Load"] = "1";
    }
    // B1098 (Eric 2026-09-06) — On distingue le COÛT D'OUVERTURE (5 premières
    // secondes après une navigation) des sondes de fond d'un onglet resté
    // ouvert. Les mélanger faisait grimper le ratio d'une page ouverte deux
    // heures à 150 appels par affichage, d'où des alertes pour rien.
    config.headers["X-Knoria-Page-Phase"] =
      Date.now() - _lastNavAt <= LOAD_WINDOW_MS ? "load" : "idle";
  } catch { /* SSR-safe */ }
  return config;
});

// ─────────────────────────────────────────────────────────────────────────────
// Vincent B1018 (Eric 2026-02-26 SPEC STRICK PROD) — Single-flight adapter
// pour les GETs. Symptôme prod : la page vidéo poll `/pipeline-status` et
// `/transcripts/all` toutes les 3s ; quand une requête est lente (>3s),
// le poll suivant part alors que la précédente n'est pas encore revenue.
// Résultat : 2× 4× 6× requêtes identiques en parallèle → saturation
// pool sockets Chrome → ERR_INSUFFICIENT_RESOURCES (voir B1015).
//
// Fix : on remplace l'adapter axios par un wrapper qui déduplique les GETs
// concurrents sur la MÊME URL+params. Le 2e appel court-circuite l'HTTP
// et attend simplement la même promise que le 1er. On skippe explicitement
// via `config._noDedup = true` pour les endpoints où on veut le comportement
// classique (rare).
// ─────────────────────────────────────────────────────────────────────────────
const _inflightGets = new Map();  // key = url+params → Promise
const _buildInflightKey = (cfg) => {
  const url = cfg.url || "";
  const params = cfg.params ? JSON.stringify(cfg.params) : "";
  return `${url}|${params}`;
};
// ─────────────────────────────────────────────────────────────────────────────
// B1052 (Eric 2026-09-04 SPEC STRICK PROD) — ⚠️ CAUSE RACINE DE L'INCIDENT
// « impossible de se connecter » (2026-09-02 → 2026-09-04).
//
// Le code d'origine faisait :
//     const _originalAdapter = axios.defaults.adapter;   // ❌
//
// Ça supposait que `axios.defaults.adapter` est UNE FONCTION. C'était vrai
// jusqu'à axios 1.6. Depuis axios 1.7, la valeur par défaut est un
// **tableau de noms** : `["xhr", "http", "fetch"]`. Notre `package.json`
// déclare `"axios": "^1.8.4"` → un `yarn install` pendant un déploiement a
// résolu axios 1.13.2 et **chaque appel axios de l'application s'est mis à
// mourir instantanément** avec :
//
//     TypeError: _originalAdapter is not a function
//
// Conséquences observées (prouvées au navigateur le 2026-09-04) :
//   • AUCUNE requête axios ne quittait le navigateur. Sur la page de login,
//     `/api/csrf` et `/api/auth/me` n'apparaissaient tout simplement PAS
//     dans l'onglet réseau. Seuls les appels en `fetch()` natif
//     (BootHealthCheck, ConnectionDiagnostic) passaient → d'où le
//     diagnostic trompeur « Ping API 200 / Santé serveur 200 » alors que
//     le login était mort.
//   • L'erreur n'ayant ni `.response` ni `.config`, l'interceptor la
//     classait « échec réseau » → retry ×8 → 6 échecs en < 10 s →
//     circuit-breaker OPEN → bandeau « Backend Knoria injoignable ».
//   • Le POST `/api/auth/login` était rejeté AVANT même d'être émis, avec
//     un message d'erreur différent selon quelle protection se déclenchait
//     la première (« Vérifiez votre internet », « Requête interrompue »,
//     « Backend intermittent », « Session expirée »). D'où l'impression
//     d'une cause différente à chaque tentative de correction : les
//     symptômes changeaient, la cause était toujours celle-ci.
//
// FIX : résoudre l'adapter réel via `axios.getAdapter()` (l'API officielle,
// qui accepte fonction / string / tableau). Et surtout : si la résolution
// échoue pour quelque raison que ce soit, on **n'installe PAS** le wrapper.
// La déduplication est une OPTIMISATION — elle ne doit jamais pouvoir
// empêcher une requête de partir.
// ─────────────────────────────────────────────────────────────────────────────
const _defaultAdapterSpec = axios.defaults.adapter;

const _resolveRealAdapter = () => {
  try {
    if (typeof _defaultAdapterSpec === "function") return _defaultAdapterSpec;
    if (typeof axios.getAdapter === "function") {
      const resolved = axios.getAdapter(_defaultAdapterSpec);
      if (typeof resolved === "function") return resolved;
    }
  } catch { /* voir le garde-fou ci-dessous */ }
  return null;
};

const _originalAdapter = _resolveRealAdapter();

const _dedupAdapter = async (cfg) => {
  const method = (cfg.method || "get").toLowerCase();
  // B1042 (Eric 2026-09-02 SPEC STRICK PROD) — Skip dedup si le caller
  // apporte son propre AbortController (`signal`) ou CancelToken. Motif :
  // le dedup fait piggyback plusieurs callers sur UNE seule promesse ;
  // si le caller primaire (celui qui a lancé la vraie requête HTTP)
  // unmount et abort son signal, l'axios sous-jacent est annulé et
  // TOUS les callers piggybackés se retrouvent avec ERR_CANCELED —
  // même ceux qui n'ont pas annulé. Ils retentent alors, refont un
  // dedup, re-abortent, etc. → boucle infinie « Tentative 1/8 » (voir
  // RCA production 2026-09-02, outage 15h). Fix : chaque caller avec
  // signal/cancelToken émet sa propre requête (pas de piggyback).
  //
  // Skip aussi les long-polls de transcription (pattern URL) car ils
  // ont une durée > polling interval → dedup risque d'introduire des
  // couplages entre polls successifs (ceinture + bretelles vs RCA).
  const url = cfg.url || "";
  const isLongPoll = /\/api\/videos\/[^/]+\/transcription-progress\b/.test(url)
    || /\/api\/videos\/[^/]+\/pipeline-status\b/.test(url);
  if (method !== "get" || cfg._noDedup || cfg.signal || cfg.cancelToken || isLongPoll) {
    return _originalAdapter(cfg);
  }
  const key = _buildInflightKey(cfg);
  const existing = _inflightGets.get(key);
  if (existing) {
    // 2e appel identique en vol → on réutilise la promesse (aucun HTTP émis).
    return existing;
  }
  const promise = _originalAdapter(cfg).finally(() => {
    // Nettoyage à la résolution (ou rejet) pour permettre au prochain
    // poll de faire une vraie requête neuve.
    _inflightGets.delete(key);
  });
  _inflightGets.set(key, promise);
  return promise;
};

// B1052 — Installation CONDITIONNELLE. Si l'adapter réel n'a pas pu être
// résolu (nouvelle version d'axios qui change encore la convention, build
// exotique…), on laisse axios totalement intact : l'app perd la
// déduplication des GETs (une optimisation) mais reste 100 % fonctionnelle.
// C'est exactement ce garde-fou qui manquait et qui a coûté 2 jours de
// production : une optimisation placée sur le chemin critique sans repli.
if (_originalAdapter) {
  axios.defaults.adapter = _dedupAdapter;
} else {
  // eslint-disable-next-line no-console
  console.error(
    "[axios] B1052 — adapter par défaut non résolvable (%o) : déduplication des GETs DÉSACTIVÉE. "
    + "L'application reste fonctionnelle. Vérifie la version d'axios.",
    _defaultAdapterSpec
  );
}

// Exposé pour les tests unitaires (non utilisé en runtime).
export const __B1052_INTERNALS = {
  _resolveRealAdapter,
  _dedupAdapter,
  get realAdapter() { return _originalAdapter; },
  get installed() { return axios.defaults.adapter === _dedupAdapter; },
};

// ── Single-flight refresh: if N concurrent requests get 401 simultaneously,
// they share the SAME refresh promise instead of stampeding the backend with
// N rotate-refresh-token calls (which would also trigger replay-attack detection).
let _refreshPromise = null;
const _AUTH_FAIL_EVENT = "knoria:auth-failed";

const _shouldAttemptRefresh = (error) => {
  const status = error?.response?.status;
  const url = error?.config?.url || "";
  const cfg = error?.config || {};
  if (status !== 401) return false;
  if (cfg._retried) return false;
  // Don't try to refresh on the auth endpoints themselves (avoids loops)
  if (url.includes("/api/auth/refresh")) return false;
  if (url.includes("/api/auth/login")) return false;
  if (url.includes("/api/auth/register")) return false;
  if (url.includes("/api/auth/logout")) return false;
  return true;
};

const _doRefresh = async () => {
  if (!_refreshPromise) {
    _refreshPromise = (async () => {
      try {
        await axios.post(
          `${API}/api/auth/refresh`,
          {},
          { withCredentials: true, _skipRefreshInterceptor: true }
        );
        // Vincent P1.10 (Eric 2026-07-31 SPEC STRICK PROD) — Le refresh pose
        // le nouveau cookie httpOnly côté serveur ; on n'a plus rien à
        // persister côté client. Le prochain appel enverra automatiquement
        // le nouveau cookie.
        return { ok: true };
      } catch (err) {
        return { ok: false, status: err?.response?.status };
      } finally {
        // Clear after a short tick so other 401s in the same micro-batch share this result.
        setTimeout(() => { _refreshPromise = null; }, 0);
      }
    })();
  }
  return _refreshPromise;
};

axios.interceptors.response.use(
  (response) => {
    // B1041 — Toute réponse réussie referme le breaker et vide la fenêtre.
    try { _resetBreakerOnSuccess(); } catch { /* ignore */ }
    // Vincent B589 (Eric 2026-07-27 20h) — Dispatch un event global
    // `favorites:updated` sur chaque mutation POST/DELETE /api/favorites
    // pour que le Dashboard rafraîchisse sa liste sans reload.
    // Eric 2026-07-28 (iter169) — Étendu à `recommendations:invalidate`
    // sur les events qui influencent « Ta prochaine action » : soumission
    // de quiz et update de progression vidéo (watched_ok, complete, quiz
    // score). Le Dashboard écoute → refetch `/api/recommendations`.
    try {
      const cfg = response?.config || {};
      const url = cfg.url || "";
      const method = (cfg.method || "").toLowerCase();
      if ((method === "post" || method === "delete") && /\/api\/favorites(\b|\/)/.test(url)) {
        window.dispatchEvent(new Event("favorites:updated"));
        window.dispatchEvent(new Event("progress:invalidate"));  // iter170 : compteur favoris hero
      }
      if (method === "post" && (
        /\/api\/quiz\/[^/]+\/submit\b/.test(url) ||
        /\/api\/videos\/[^/]+\/watch-progress\b/.test(url) ||
        /\/api\/onboarding\/pathways\/[^/]+\/steps\/[^/]+\/quiz\b/.test(url)
      )) {
        window.dispatchEvent(new Event("recommendations:invalidate"));
        // Eric 2026-07-28 (iter170) — Les MiniStats du hero (videos_viewed,
        // quizzes_completed, videos_quiz_passed, deltas hebdo) sont
        // impactées par les mêmes events → refresh en même temps.
        window.dispatchEvent(new Event("progress:invalidate"));
      }
    } catch { /* silencieux */ }
    return response;
  },
  async (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const silent = error?.config?.silent === true;
    const cfg = error?.config || {};

    // ─────────────────────────────────────────────────────────────────────
    // B1042 (Eric 2026-09-02 SPEC STRICK PROD) — Ne JAMAIS retenter une
    // requête annulée (AbortController / axios.CancelToken).
    // ─────────────────────────────────────────────────────────────────────
    // Contexte RCA production 2026-09-02 : outage 15h côté client alors
    // que le backend prod servait 200 en continu. Cause : plusieurs
    // hooks (usePipelineStatus, VideoDetailPage, ConnectionDiagnostic)
    // passent `signal: controller.signal` à axios pour tuer les polls
    // au unmount. Ces requêtes annulées rejettent avec
    // `error.code === "ERR_CANCELED"` (ou `axios.isCancel(err) === true`)
    // et SANS `error.response`. Notre interceptor les classait
    // « niveau réseau » → retry × 8 → boucle infinie « Tentative 1/8 »
    // (voir /app/deployer-agent-docs/RCA_6df6815c…).
    //
    // Fix : détecter les annulations et propager l'erreur telle quelle,
    // sans retry, sans banner, sans event. C'est un signal EXPLICITE
    // du caller — jamais un « transient network ».
    if (axios.isCancel && axios.isCancel(error)) {
      error._isCanceled = true;
      return Promise.reject(error);
    }

    // Bogue 1202 (Eric 2026-06-26) — Erreur de PASSERELLE (502/503/504/52x).
    // Les logs du conteneur déployé ne sont pas consultables et, quand
    // l'origine ne répond pas, le serveur n'a rien à journaliser : seul le
    // navigateur peut dire quelle requête a échoué, avec quel code et en
    // combien de temps. Rapport en `fetch`, sans await, sans effet de bord.
    try { reportGatewayError(error); } catch { /* jamais bloquant */ }
    if (error?.code === "ERR_CANCELED" || error?.message === "canceled") {
      error._isCanceled = true;
      return Promise.reject(error);
    }

    // B1046 — Aborts induits par une NAVIGATION (unload). Signature : pas de
    // `.response` + un event navigation réel (pagehide/beforeunload/unload).
    //
    // B1051 (Eric 2026-09-04) — Note historique : la cause de ces aborts
    // n'était PAS `emergent-main.js` comme supposé en B1046, mais NOTRE
    // propre « version gate » dans `public/index.html`, qui forçait un
    // `location.replace()` 4 s après chaque chargement parce que
    // `/api/version` renvoyait "unknown" en prod. Ce gate a été SUPPRIMÉ
    // (voir backend/routers/version.py pour le RCA complet). Ce filtre
    // reste utile pour les vraies navigations (l'utilisateur quitte la
    // page pendant qu'une requête est en vol), mais il ne devrait plus
    // JAMAIS se déclencher sur la page de login.
    if (!error?.response && _isNavigationAbort(error)) {
      error._isCanceled = true;
      error._navigationAbort = true;
      return Promise.reject(error);
    }

    // ─────────────────────────────────────────────────────────────────────
    // Éric (2026-06-25) — 403 « CSRF token verification failed ».
    //
    // Le cookie `XSRF-TOKEN` a une durée de vie : sur un onglet resté ouvert
    // (ou une session restaurée par le navigateur), il peut avoir disparu
    // alors que le cookie de session, lui, est toujours valide. L'interceptor
    // de requête ci-dessus n'ajoute alors AUCUN header — il ne l'ajoute que
    // si le cookie existe — et le serveur rejette la mutation avec un 403
    // technique en anglais. L'utilisateur, lui, voit « code d'erreur 403 »
    // sur un geste parfaitement légitime (Éric en collant sa clé OpenAI).
    //
    // On rebootstrappe donc le jeton et on rejoue la requête UNE seule fois.
    // Si le 403 persiste, il est propagé normalement.
    // ─────────────────────────────────────────────────────────────────────
    if (status === 403 && !cfg._csrfRetried) {
      let body = "";
      try {
        const data = error?.response?.data;
        body = typeof data === "string" ? data : JSON.stringify(data || "");
      } catch { body = ""; }
      if (/csrf/i.test(body)) {
        cfg._csrfRetried = true;
        try {
          await axios.get(`${API}/api/csrf`, {
            withCredentials: true, silent: true, _skipRefreshInterceptor: true,
          });
          const fresh = _getCookie("XSRF-TOKEN");
          if (fresh) {
            cfg.headers = cfg.headers || {};
            cfg.headers["X-XSRF-TOKEN"] = fresh;
          }
          return axios(cfg);
        } catch { /* on propage l'erreur d'origine */ }
      }
    }

    // ─────────────────────────────────────────────────────────────────────
    // Eric (2026-05-19) — Bouclier anti-pannes Cloudflare / cold starts.
    // ─────────────────────────────────────────────────────────────────────
    // Vincent a vu un "Initialisation impossible" (HTTP 520) en plein milieu
    // d'une démo. Le 520 est une erreur transitoire de Cloudflare (origine
    // injoignable temporairement, cold-start backend, hoquet réseau). Au lieu
    // de propager l'erreur à l'utilisateur, on retry SILENCIEUSEMENT 3 fois
    // avec backoff exponentiel (300ms, 800ms, 2000ms). Couvre :
    //   • 502 Bad Gateway      (Cloudflare ↔ origin temporairement KO)
    //   • 503 Service Unavailable (backend overloaded / restart)
    //   • 504 Gateway Timeout  (origin trop lent)
    //   • 520/521/522/523/524  (famille Cloudflare unknown/down/timeout)
    //   • 408 Request Timeout  (réseau client)
    //   • Erreurs sans réponse (DNS, ECONNREFUSED, TLS handshake, etc.)
    // On ne retry PAS les codes "définitifs" (4xx applicatifs, 500 logique).
    // Eric 2026-07-17 (SPEC STRICK — Vincent 520 stabilité perm.) —
    // Renforcement du bouclier :
    //   • maxRetries : 3 → 6 (couvre 60s de redémarrage K8s)
    //   • Backoff max : 4s → 12s
    //   • Émission d'un event "knoria:connection-recovering" à chaque retry
    //     pour permettre à un banner UI d'informer l'utilisateur discrètement
    //     (voir ConnectionRecoveringBanner.js)
    // Vincent B665 (Eric 2026-08-05 SPEC STRICK PROD) — Nouvelle vague de
    // 520 pendant les fenêtres de déploiement production. Les pods K8s
    // mettent parfois > 40s à warmer (import Python + connexion Mongo +
    // ensure_indexes), et 6 retries à 12s max = ~35s de patience, insuffisant.
    // Bump : 6 → 8 retries, cap 12s → 20s → total ~90s de tolérance. Le
    // banner UI reste actif tant qu'on retente ; il ne bascule en "failed"
    // qu'après ces 90s. En pratique, ça couvre 99 % des redéploiements.
    // Vincent B796 (Eric 2026-02-12) — HTTP 429 est RETIRÉ de la liste
    // des codes auto-retriés. Motif : 429 = « Too Many Requests », signal
    // EXPLICITE du serveur qui demande au client de ralentir. Retentait
    // à 300ms → 750ms → 1.9s empirait la situation : chaque retry
    // consommait un slot de la fenêtre 200 req/min → cascade infinie de
    // 429 → banner alarmiste « Reconnexion en cours » qui affole Vincent
    // « sans rien faire ». Désormais, un 429 est propagé au code
    // appelant (qui décide : ignorer si polling, backoff explicite si
    // interactif). Un event distinct `knoria:rate-limited` est émis
    // pour un toast informatif discret (pas le banner « Reconnexion »).
    const isTransientHttp = status && [408, 425, 502, 503, 504, 520, 521, 522, 523, 524, 525].includes(status);
    // Vincent B1015 (Eric 2026-02-26 SPEC STRICK PROD) — Détection des
    // erreurs de saturation du navigateur (`ERR_INSUFFICIENT_RESOURCES`,
    // `ERR_NETWORK_IO_SUSPENDED`, `ERR_SOCKET_NOT_CONNECTED`). Ces codes
    // sont ÉMIS PAR CHROME LUI-MÊME quand son pool de sockets/handles est
    // épuisé — c'est un signal explicite « ralentis ». Notre bouclier
    // retenait ces erreurs comme un simple network-level → 8 retries,
    // chacun échouant instantanément avec le même code, générant à son
    // tour 8 retries en cascade. Symptôme : banner « Reconnexion en cours »
    // bloqué à 2 utilisateurs alors que le serveur va bien. Fix : on
    // exclut ces codes du retry (aligné sur la logique B796 pour 429).
    // Un event `knoria:browser-throttled` est émis pour permettre à l'UI
    // de baisser sa cadence de polling au lieu d'insister.
    const errCode = (error?.code || "") + " " + (error?.message || "");
    const isBrowserThrottled = /ERR_INSUFFICIENT_RESOURCES|ERR_NETWORK_IO_SUSPENDED|ERR_SOCKET_NOT_CONNECTED/i.test(errCode);
    const isNetworkLevel = !error.response && !isBrowserThrottled; // saturation navigateur ≠ panne réseau
    const method = (cfg.method || "get").toLowerCase();
    const isIdempotent = ["get", "head", "options"].includes(method) || cfg._allowRetry === true;
    const maxRetries = cfg._maxRetries ?? 8;
    cfg._retryCount = cfg._retryCount || 0;

    if (isBrowserThrottled) {
      // Ne PAS retenter — on informe l'UI et on propage l'erreur pour que
      // le poller puisse espacer ses appels (ex : passer de 5s à 30s).
      try {
        window.dispatchEvent(new CustomEvent("knoria:browser-throttled", {
          detail: { url, code: error?.code || null },
        }));
      } catch { /* ignore */ }
      // Vincent B1016 (Eric 2026-02-26 SPEC STRICK PROD) — Télémétrie
      // backend : fire-and-forget POST vers `/api/telemetry/browser-throttle`
      // pour compter les events en prod et repérer les régressions.
      // Debounce local (1 event max/5s pour la même URL) pour éviter
      // d'alourdir davantage le pool sockets.
      try {
        _reportBrowserThrottle(url, error?.code || null);
      } catch { /* ignore */ }
      return Promise.reject(error);
    }

    if ((isTransientHttp || isNetworkLevel) && isIdempotent && cfg._retryCount < maxRetries) {
      // B1041 — Court-circuit si le breaker est ouvert : plus aucun retry
      // tant que le backend n'a pas donné signe de vie. On rejette
      // immédiatement avec un message clair. Ne s'applique qu'aux erreurs
      // niveau réseau (isNetworkLevel) — les 5xx applicatifs peuvent
      // continuer à retry normalement même si on soupçonne un outage
      // (car la réponse HTTP prouve que le backend répond partiellement).
      if (isNetworkLevel && _isBreakerOpen()) {
        error._friendlyMessage = "Backend Knoria injoignable — nouvelle tentative dans 30 s. Ta session est intacte.";
        error._isTransient = true;
        error._breakerOpen = true;
        return Promise.reject(error);
      }
      // B1041 — Enregistre l'échec réseau et déclenche le breaker si seuil dépassé
      if (isNetworkLevel) {
        _recordNetworkFailure();
        if (_maybeTripBreaker()) {
          // Breaker vient de trip → on rejette celui-ci aussi (pas de retry)
          error._friendlyMessage = "Backend Knoria injoignable — nouvelle tentative dans 30 s. Ta session est intacte.";
          error._isTransient = true;
          error._breakerOpen = true;
          return Promise.reject(error);
        }
      }
      cfg._retryCount += 1;
      // Backoff : 300ms → 750ms → 1.9s → 4.7s → 11.7s → 20s(cap) × 3 = ~90s total.
      const backoff = Math.min(300 * Math.pow(2.5, cfg._retryCount - 1), 20000);
      // eslint-disable-next-line no-console
      console.warn(
        `[axios] transient ${status || "network"} on ${method.toUpperCase()} ${url || "(url vide)"}`
        + ` — retry ${cfg._retryCount}/${maxRetries} in ${Math.round(backoff)}ms`
        // B1051 — Diagnostic : sans le code/message on ne peut PAS identifier
        // l'origine des échecs « url vide » qui ouvraient le circuit-breaker
        // à tort sur la page de login (2 jours de debug perdus).
        + ` [code=${error?.code || "?"} msg=${error?.message || "?"} hasConfig=${!!error?.config}]`
      );
      // Émettre un event pour permettre au banner de s'afficher
      try {
        window.dispatchEvent(new CustomEvent("knoria:connection-recovering", {
          detail: {
            attempt: cfg._retryCount,
            maxRetries,
            nextRetryMs: backoff,
            status: status || null,
            url,
          },
        }));
      } catch { /* ignore */ }
      // Tâche 1434 — On rapporte au serveur, à la PREMIÈRE tentative
      // seulement : c'est le constat initial qui porte l'information (quelle
      // url, après combien de temps), les réessais ne font que la répéter.
      // Seules les erreurs de passerelle sont rapportées — une panne réseau
      // du poste de travail ne concerne pas le serveur.
      if (isTransientHttp && cfg._retryCount === 1) {
        try {
          _rapporterErreurPasserelle(
            error, url, status,
            cfg._startedAt ? Date.now() - cfg._startedAt : 0,
          );
        } catch { /* ignore */ }
      }
      await _sleep(backoff);
      return axios(cfg);
    }

    // Si on a épuisé tous les retries, émettre un event de récupération finale
    if ((isTransientHttp || isNetworkLevel) && cfg._retryCount >= maxRetries) {
      try {
        window.dispatchEvent(new CustomEvent("knoria:connection-recovery-failed", {
          detail: { status: status || null, url, attempts: cfg._retryCount },
        }));
      } catch { /* ignore */ }
    }

    // Vincent B796 (Eric 2026-02-12) — Gestion dédiée du HTTP 429.
    // On NE re-tente PAS (car cela consomme davantage de slots de la
    // fenêtre 200 req/min et empire la situation). On émet un event
    // spécifique `knoria:rate-limited` pour permettre à l'UI d'afficher
    // un toast INFORMATIF et non-alarmiste (pas le banner « Reconnexion
    // en cours » qui laissait Vincent croire à un bug session). Le code
    // appelant recevra l'erreur 429 et peut soit ignorer (cas polling
    // silencieux), soit afficher un feedback ciblé.
    if (status === 429) {
      try {
        // Respecte l'entête Retry-After si présent (RFC 6585) ; sinon
        // 60 s par défaut (correspond à la fenêtre de rate-limit
        // globale du backend). Utilisé par le toast pour le compte à
        // rebours et par les pollers pour espacer leurs requêtes.
        const retryAfterHeader = error.response?.headers?.["retry-after"];
        const retryAfterMs = retryAfterHeader
          ? (isNaN(Number(retryAfterHeader))
              ? Math.max(0, new Date(retryAfterHeader).getTime() - Date.now())
              : Number(retryAfterHeader) * 1000)
          : 60000;
        window.dispatchEvent(new CustomEvent("knoria:rate-limited", {
          detail: { url, retryAfterMs, message: error.response?.data?.detail || null },
        }));
      } catch { /* ignore */ }
      error._friendlyMessage = "Trop de requêtes en peu de temps — patiente environ 1 minute avant de réessayer.";
      error._isRateLimited = true;
      return Promise.reject(error);
    }

    // ── 401 → try to refresh transparently, then replay the failed request.
    if (_shouldAttemptRefresh(error) && !error?.config?._skipRefreshInterceptor) {
      const refresh = await _doRefresh();
      if (refresh.ok) {
        // Vincent P1.10 — Le rejeu envoie le nouveau cookie httpOnly
        // automatiquement (via withCredentials). Plus besoin d'injecter
        // le token dans le header Authorization.
        const retried = { ...error.config, _retried: true };
        return axios(retried);
      }
      // Refresh itself failed → genuine logout. Notify AuthContext.
      try { window.dispatchEvent(new CustomEvent(_AUTH_FAIL_EVENT, { detail: { reason: "refresh_failed" } })); } catch {}
      // Annotate and propagate (component handlers can show their own UI).
      error._isAuthFailure = true;
      return Promise.reject(error);
    }

    // ── Non-auth errors: annotate with a friendly message and emit a banner event,
    // but DO NOT trigger any logout. Components decide what to do.
    if (!error.response) {
      error._friendlyMessage = "Connexion temporairement indisponible. Vous restez connecté — réessayez dans quelques secondes.";
      error._isTransient = true;
      if (!silent) {
        try { window.dispatchEvent(new CustomEvent("knoria:server-error", { detail: { kind: "network", url } })); } catch {}
      }
    } else if (status >= 500) {
      error._friendlyMessage = `Erreur serveur (${status}). Vous restez connecté — l'équipe a été notifiée.`;
      error._isServerError = true;
      if (!silent) {
        try { window.dispatchEvent(new CustomEvent("knoria:server-error", { detail: { kind: "server", status, url } })); } catch {}
      }
    } else if (status === 408 || status === 504) {
      error._friendlyMessage = "Le serveur a mis trop de temps à répondre. Réessayez.";
      error._isTransient = true;
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

// (Sleep helper hoisted above the interceptor at the top of this file.)

export function AuthProvider({ children }) {
  // user: null = "auth state unknown / checking", false = "definitively logged out", object = authenticated
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Surfaces "we can't reach the backend right now" without flipping the user out.
  const [authReachable, setAuthReachable] = useState(true);
  const initialCheckRanRef = useRef(false);

  // Single source of truth for the /auth/me call. NEVER sets user=false on transient errors.
  const performAuthCheck = useCallback(async ({ silent = false } = {}) => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true, silent });
      setUser(data);
      setAuthReachable(true);
      return { ok: true };
    } catch (err) {
      const status = err?.response?.status;
      // 401 hits the response interceptor first, which tries refresh+retry. If we get
      // here with status=401, refresh failed → user is definitively logged out.
      if (status === 401 || status === 403 || err?._isAuthFailure) {
        // Vincent P1.10 — Plus de localStorage à nettoyer ; le cookie est
        // déjà invalidé (soit par expiration TTL, soit par le backend).
        setUser(false);
        setAuthReachable(true);
        return { ok: false, expired: true };
      }
      // Transient (network / 5xx / timeout). DO NOT touch user — keep current state.
      setAuthReachable(false);
      return { ok: false, transient: true, status };
    }
  }, []);

  // Initial load: retry transient failures with exponential backoff so a momentary
  // hiccup doesn't kick a logged-in user to /login the moment they open Knoria.
  // Eric 2026-05-07 — DO NOT use a `cancelled` flag here. In React 18 StrictMode,
  // the effect mounts → cleanup → mounts again. The cleanup of the first mount
  // would set `cancelled=true` before the async work could finish, and the second
  // mount is skipped (due to `initialCheckRanRef`), leaving `setLoading(false)`
  // never reached → permanent spinner. The ref guard alone is the right pattern.
  useEffect(() => {
    if (initialCheckRanRef.current) return;
    initialCheckRanRef.current = true;
    (async () => {
      // Vincent P1 CSRF (Eric 2026-07-31) — Bootstrap le cookie XSRF-TOKEN
      // avant toute mutation. GET fire-and-forget, non bloquant.
      try {
        await axios.get(`${API}/api/csrf`, { withCredentials: true, silent: true });
      } catch { /* non bloquant, le middleware ré-émet le cookie sur chaque GET */ }
      const backoffsMs = [0, 800, 2000, 4000]; // ~7s total before giving up
      for (let i = 0; i < backoffsMs.length; i++) {
        if (backoffsMs[i] > 0) await _sleep(backoffsMs[i]);
        const r = await performAuthCheck({ silent: true });
        if (r.ok || r.expired) {
          // Definitive answer (logged in OR genuinely logged out).
          setLoading(false);
          return;
        }
        // Transient — try again.
      }
      // After all retries the backend is still unreachable. Resolve loading so the UI
      // can render. Keep `user=null` (NOT false) so ProtectedRoute shows a "connection
      // unavailable, retry" banner instead of redirecting to /login.
      setLoading(false);
    })();
  }, [performAuthCheck]);

  // 4-min heartbeat keeps the session warm and detects backend-side revocations.
  // Heartbeat NEVER causes a logout on transient errors — only refresh-failed 401s do.
  useEffect(() => {
    if (!user || user === false) return;
    let cancelled = false;
    const heartbeat = async () => {
      if (cancelled) return;
      const r = await performAuthCheck({ silent: true });
      if (r.transient && !cancelled) {
        // Schedule a quick retry in 30s on transient errors.
        setTimeout(heartbeat, 30000);
      }
    };
    const id = setInterval(heartbeat, 4 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, performAuthCheck]);

  // React to auth-failed events from the axios interceptor (refresh failed).
  useEffect(() => {
    const onAuthFail = () => {
      // Vincent P1.10 — Cookie déjà invalidé côté backend / expiré. Rien à
      // nettoyer côté client.
      setUser(false);
      setAuthReachable(true);
    };
    window.addEventListener(_AUTH_FAIL_EVENT, onAuthFail);
    return () => window.removeEventListener(_AUTH_FAIL_EVENT, onAuthFail);
  }, []);

  // Public API.
  const checkAuth = useCallback((opts) => performAuthCheck(opts || {}), [performAuthCheck]);

  // Manual reconnect attempt (used by the "Connection unavailable" banner).
  const retryAuth = useCallback(async () => {
    setLoading(true);
    setAuthReachable(true);
    const r = await performAuthCheck({ silent: false });
    if (r.transient) {
      setLoading(false);
      return false;
    }
    setLoading(false);
    return true;
  }, [performAuthCheck]);

  const login = async (email, password, totp_code = null) => {
    try {
      const body = { email, password };
      if (totp_code) body.totp_code = totp_code;
      await axios.post(
        `${API}/api/auth/login`,
        body,
        {
          withCredentials: true,
          // B1051 (Eric 2026-09-04) — Le login est la requête la plus
          // critique de l'app : si elle échoue sur un hoquet transitoire
          // (502/520 pendant une fenêtre de déploiement, cold start du pod),
          // l'utilisateur voit un message d'erreur et croit que ses
          // identifiants sont mauvais. On autorise donc explicitement le
          // retry de ce POST (l'interceptor ne retente les POST que si
          // `_allowRetry` est vrai). 3 tentatives max ≈ 3 s de patience —
          // assez pour absorber un hoquet, assez court pour ne pas figer
          // le bouton. Les 401/403 (mauvais mdp, TOTP requis) ne sont PAS
          // dans la liste des codes transitoires → aucun retry, réponse
          // immédiate.
          _allowRetry: true,
          _maxRetries: 3,
        }
      );
      // Vincent P1.10 — Le backend a posé le cookie httpOnly ; on lit le
      // profil via /auth/me pour peupler le state React (le body de /login
      // ne contient plus le token).
      await performAuthCheck({ silent: false });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "pending_approval") throw new Error("PENDING_APPROVAL");
      if (detail === "rejected") throw new Error("REJECTED");
      // Vincent #208 — 2FA required (1er step OK, password verifié, mais besoin du code TOTP)
      if (err.response?.status === 403 && detail === "totp_required") throw new Error("TOTP_REQUIRED");
      throw err;
    }
  };

  const register = async (email, password, name, partner_code, company_name, registration_reason, org_invite_token) => {
    const payload = { email, password, name };
    if (partner_code) payload.partner_code = partner_code;
    if (company_name) payload.company_name = company_name;
    if (registration_reason) payload.registration_reason = registration_reason;
    // Vincent 2026-06-03 (Eric — item 13) — Token d'invitation : si présent,
    // l'utilisateur est auto-rattaché à l'org cible (pas de pending approval).
    if (org_invite_token) payload.org_invite_token = org_invite_token;
    const { data } = await axios.post(
      `${API}/api/auth/register`,
      payload,
      { withCredentials: true }
    );
    if (data.account_status === "pending_approval") return data;
    // Vincent P1.10 — Cookie httpOnly déjà posé par le backend ; on peuple
    // simplement le state React avec le user retourné par le body.
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Logout is best-effort: even if the call fails (network), we still clear local state.
    }
    // Vincent P1.10 — Backend a supprimé les cookies via delete_cookie().
    // Plus rien à faire côté localStorage.
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        authReachable,
        login,
        register,
        logout,
        checkAuth,
        refreshUser: checkAuth,
        retryAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext };

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Vincent #98 (Eric 2026-05-12) — Hook qui retourne un helper `roleLabel(canonical)`
// affichant le libellé personnalisé de l'org pour le rôle donné. Fonctionne avec :
//  - canoniques DB : "founder", "admin", "concepteur", "employee_advanced",
//    "employee_trained", "employee_new", "employee", "viewer"
//  - access_level : "debutant" → employee_new, "intermediaire" → employee_trained,
//    "avance" → employee_advanced
//  - alias view_as_role : "user:debutant" → employee_new, etc.
// Tombe sur le libellé canonique par défaut si rien n'est custom côté org.
const _CANONICAL_FALLBACK = {
  founder: "Fondateur",
  admin: "Admin",
  concepteur: "Concepteur",
  employee_advanced: "Employé avancé",
  employee_trained: "Employé formé",
  employee_new: "Nouvel employé",
  employee: "Employé",
  viewer: "Visiteur",
};

const _ALIAS_TO_CANONICAL = {
  // access_level codes
  debutant: "employee_new",
  intermediaire: "employee_trained",
  avance: "employee_advanced",
  // view_as_role values
  "user:debutant": "employee_new",
  "user:intermediaire": "employee_trained",
  "user:avance": "employee_advanced",
  "user:concepteur": "concepteur",
  // raw role values from backend
  user: "employee",
  employe: "employee",
  // backward compat
  founder: "founder",
  admin: "admin",
  concepteur: "concepteur",
};

export function useRoleLabel() {
  const { user } = useAuth();
  const labels = (user && user.role_labels) || _CANONICAL_FALLBACK;
  return (input) => {
    if (!input) return "";
    const key = String(input).toLowerCase();
    const canonical = _ALIAS_TO_CANONICAL[key] || (labels[key] ? key : null);
    if (canonical && labels[canonical]) return labels[canonical];
    if (labels[key]) return labels[key];
    return _CANONICAL_FALLBACK[canonical || key] || input;
  };
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
